import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReactDOM from "react-dom/client";
import { ToastContext } from "../../Context/ToastContext";
import PayslipTemplate from "./PayslipTemplate";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const PayslipGenerator = () => {
  const { salaryId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const payslipPreviewRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [employeeSalary, setEmployeeSalary] = useState(null);
  const [payslipLabels, setPayslipLabels] = useState({
    payslipTitle: "Payslip For the Month",
    employee: {},
    bank: {},
    earnings: {},
    deductions: {},
  });
  const [companyInfo, setCompanyInfo] = useState("");
  const [editableEarnings, setEditableEarnings] = useState({});
  const [editableDeductions, setEditableDeductions] = useState({});
  const [dynamicEarnings, setDynamicEarnings] = useState([]);
  const [dynamicDeductions, setDynamicDeductions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const companyKey = sessionStorage.getItem("companyKey");

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (salaryId) {
      fetchSalaryAndEmployeeData(salaryId);
    }
  }, [salaryId]);

  useEffect(() => {
    if (
      employeeDetails &&
      employeeSalary &&
      Object.keys(payslipLabels.earnings).length > 0
    ) {
      const initialEarnings = {};
      Object.entries(payslipLabels.earnings).forEach(([id, labelInfo]) => {
        const value = getDynamicEarningsValue(labelInfo.label);
        initialEarnings[id] = value;
      });

      const initialDeductions = {};
      Object.entries(payslipLabels.deductions).forEach(([id, labelInfo]) => {
        const value = getDynamicDeductionsValue(labelInfo.label);
        initialDeductions[id] = value;
      });

      setEditableEarnings(initialEarnings);
      setEditableDeductions(initialDeductions);
    }
  }, [
    employeeDetails,
    employeeSalary,
    payslipLabels.earnings,
    payslipLabels.deductions,
    dynamicEarnings,
    dynamicDeductions,
  ]);

  const fetchSalaryAndEmployeeData = async (salaryId) => {
    try {
      setLoading(true);
      setError("");

      const salaryResponse = await fetch(
        `https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList/${salaryId}`
      );
      const salaryData = await salaryResponse.json();

      if (!salaryResponse.ok) {
        throw new Error("Failed to fetch salary data");
      }

      setEmployeeSalary(salaryData);
      parseDynamicData(salaryData);
      const employeeData = await fetchEmployeeDetailsWithRetry(
        salaryData.userId,
        salaryData.mobileNumber
      );
      setEmployeeDetails(employeeData);
      await fetchPayslipLabels(employeeData);
    } catch (err) {
      setError(err.message || "Failed to load data");
      addToast(err.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const parseDynamicData = (salaryData) => {
    try {
      // Parse dynamic earnings
      if (salaryData.dynamicEarnings) {
        const parsedEarnings = JSON.parse(salaryData.dynamicEarnings);
        setDynamicEarnings(Array.isArray(parsedEarnings) ? parsedEarnings : []);
      } else {
        setDynamicEarnings([]);
      }

      // Parse dynamic deductions
      if (salaryData.dynamicDeductions) {
        const parsedDeductions = JSON.parse(salaryData.dynamicDeductions);
        setDynamicDeductions(
          Array.isArray(parsedDeductions) ? parsedDeductions : []
        );
      } else {
        setDynamicDeductions([]);
      }
    } catch (error) {
      console.error("Error parsing dynamic data:", error);
      setDynamicEarnings([]);
      setDynamicDeductions([]);
    }
  };

  const getDynamicEarningsValue = (key) => {
    if (!key) return 0;

    // First check dynamic earnings array
    const dynamicItem = dynamicEarnings.find(
      (item) => item.key === key || item.key.toLowerCase() === key.toLowerCase()
    );
    if (dynamicItem) {
      return parseFloat(dynamicItem.value) || 0;
    }

    // Fallback to static calculation for basic salary
    if (key.toLowerCase() === "basic") {
      const basicSalary =
        parseFloat(employeeSalary?.normalcalculatedSalary) || 0;
      const overtimeSalary =
        parseFloat(employeeSalary?.otcalculatedSalary) || 0;
      return basicSalary + overtimeSalary;
    }

    return 0;
  };

  // Get dynamic deductions value by key
  const getDynamicDeductionsValue = (key) => {
    if (!key) return 0;

    // First check dynamic deductions array
    const dynamicItem = dynamicDeductions.find(
      (item) => item.key === key || item.key.toLowerCase() === key.toLowerCase()
    );
    if (dynamicItem) {
      return parseFloat(dynamicItem.value) || 0;
    }

    return 0;
  };

  // Get current earnings value (considering edit state)
  const getCurrentEarningsValue = (id, labelInfo) => {
    if (isEditing && editableEarnings && editableEarnings[id] !== undefined) {
      const value = editableEarnings[id];
      return value === "" ? 0 : parseFloat(value) || 0;
    }
    return getDynamicEarningsValue(labelInfo.label);
  };

  // Get current deductions value (considering edit state)
  const getCurrentDeductionsValue = (id, labelInfo) => {
    if (
      isEditing &&
      editableDeductions &&
      editableDeductions[id] !== undefined
    ) {
      const value = editableDeductions[id];
      return value === "" ? 0 : parseFloat(value) || 0;
    }
    return getDynamicDeductionsValue(labelInfo.label);
  };

  const fetchEmployeeDetailsWithRetry = async (
    userId,
    mobileNumber,
    maxRetries = 3
  ) => {
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }

        let employeeData = null;

        // Try PayrollEmployeeBasicDetails API first
        try {
          const basicDetailsResponse = await fetch(
            `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails?userId=${userId}`,
            {
              headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Accept: "application/json",
              },
              timeout: 10000,
            }
          );

          if (basicDetailsResponse.ok) {
            const basicDetailsData = await basicDetailsResponse.json();
            employeeData = Array.isArray(basicDetailsData)
              ? basicDetailsData[0]
              : basicDetailsData;

            if (employeeData && employeeData.id) {
              employeeData = {
                ...employeeData,
                userMobile:
                  employeeData.userMobile ||
                  employeeData.personalMobile ||
                  mobileNumber,
                fullName:
                  employeeData.fullName === "null" ? "" : employeeData.fullName,
                bankName:
                  employeeData.bankName === "null" ? "" : employeeData.bankName,
                accountNumber:
                  employeeData.accountNumber === "null"
                    ? ""
                    : employeeData.accountNumber,
                ifscCode:
                  employeeData.ifscCode === "null" ? "" : employeeData.ifscCode,
                branchName:
                  employeeData.branchName === "null"
                    ? ""
                    : employeeData.branchName,
                jobTitle:
                  employeeData.jobTitle === "null" ? "" : employeeData.jobTitle,
                dateOfJoining:
                  employeeData.dateOfJoining === "null"
                    ? ""
                    : employeeData.dateOfJoining,
                basicSalary:
                  employeeData.basicSalary === "null"
                    ? ""
                    : employeeData.basicSalary,
              };
            }
          }
        } catch (basicDetailsErr) {
          // Handle error silently and try registration API
        }

        // If basic details failed, try registration API
        if (!employeeData) {
          // Try by mobile number first
          if (mobileNumber) {
            try {
              const mobileResponse = await fetch(
                `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers?mobile_number=${mobileNumber}`,
                {
                  headers: {
                    "Cache-Control": "no-cache",
                    Pragma: "no-cache",
                    Accept: "application/json",
                  },
                  timeout: 10000,
                }
              );

              if (mobileResponse.ok) {
                const mobileData = await mobileResponse.json();
                const regEmployee = Array.isArray(mobileData)
                  ? mobileData[0]
                  : mobileData;

                if (regEmployee && regEmployee.id) {
                  employeeData = {
                    id: regEmployee.id,
                    fullName: regEmployee.full_name,
                    employeeId: regEmployee.employee_id,
                    userMobile: regEmployee.mobile_number,
                    mobile_number: regEmployee.mobile_number,
                    personalEmail: regEmployee.email,
                    jobTitle: "N/A",
                    department: "N/A",
                    dateOfJoining: "N/A",
                    workLocation: regEmployee.locationName || "N/A",
                    bankName: "N/A",
                    accountNumber: "N/A",
                    ifscCode: "N/A",
                    branchName: "N/A",
                    earnings: regEmployee.earnings,
                    deductions: regEmployee.deductions,
                  };
                }
              }
            } catch (mobileErr) {
              // Handle error silently
            }
          }

          // Try by userId if mobile search failed
          if (!employeeData) {
            try {
              const userIdResponse = await fetch(
                `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers?userId=${userId}`,
                {
                  headers: {
                    "Cache-Control": "no-cache",
                    Pragma: "no-cache",
                    Accept: "application/json",
                  },
                  timeout: 10000,
                }
              );

              if (userIdResponse.ok) {
                const userIdData = await userIdResponse.json();
                const regEmployee = Array.isArray(userIdData)
                  ? userIdData[0]
                  : userIdData;

                if (regEmployee && regEmployee.id) {
                  employeeData = {
                    id: regEmployee.id,
                    fullName: regEmployee.full_name,
                    employeeId: regEmployee.employee_id,
                    userMobile: regEmployee.mobile_number,
                    mobile_number: regEmployee.mobile_number,
                    personalEmail: regEmployee.email,
                    jobTitle: "N/A",
                    department: "N/A",
                    dateOfJoining: "N/A",
                    workLocation: regEmployee.locationName || "N/A",
                    bankName: "N/A",
                    accountNumber: "N/A",
                    ifscCode: "N/A",
                    branchName: "N/A",
                    earnings: regEmployee.earnings,
                    deductions: regEmployee.deductions,
                  };
                }
              }
            } catch (userIdErr) {
              // Handle error silently
            }
          }
        }

        // If we have employee data, process earnings/deductions settings
        if (employeeData) {
          // Get earnings/deductions settings if not available
          if (!employeeData.earnings || !employeeData.deductions) {
            try {
              const regResponse = await fetch(
                `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers?mobile_number=${
                  employeeData.userMobile || employeeData.personalMobile
                }`,
                {
                  headers: {
                    "Cache-Control": "no-cache",
                    Pragma: "no-cache",
                    Accept: "application/json",
                  },
                  timeout: 10000,
                }
              );

              if (regResponse.ok) {
                const regData = await regResponse.json();
                const regEmployee = Array.isArray(regData)
                  ? regData[0]
                  : regData;
                if (regEmployee) {
                  employeeData.earnings = regEmployee.earnings;
                  employeeData.deductions = regEmployee.deductions;
                }
              }
            } catch (regErr) {
              // Handle error silently
            }
          }

          // Parse settings
          const earningsSettings = parseEmployeeSettings(
            employeeData.earnings,
            {
              Basic: true,
              "House Rent Allowance": true,
              Incentive: true,
            },
            "earnings"
          );

          const deductionsSettings = parseEmployeeSettings(
            employeeData.deductions,
            {
              "Income Tax": true,
              "Provident Fund": true,
            },
            "deductions"
          );

          const processedEmployeeData = {
            ...employeeData,
            earningsSettings,
            deductionsSettings,
            userMobile:
              employeeData.userMobile ||
              employeeData.personalMobile ||
              employeeData.mobile_number ||
              mobileNumber,
          };

          return processedEmployeeData;
        }

        throw new Error("No employee data found in any API");
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          break;
        }
      }
    }

    // All attempts failed, return default data to keep UI functional
    const defaultDetails = {
      id: userId,
      fullName: "Employee Not Found",
      employeeId: userId,
      userMobile: mobileNumber || "N/A",
      personalEmail: "N/A",
      jobTitle: "N/A",
      department: "N/A",
      dateOfJoining: "N/A",
      workLocation: "N/A",
      bankName: "N/A",
      accountNumber: "N/A",
      ifscCode: "N/A",
      branchName: "N/A",
      earningsSettings: {
        Basic: true,
        "House Rent Allowance": true,
        Incentive: true,
      },
      deductionsSettings: { "Income Tax": true, "Provident Fund": true },
    };

    return defaultDetails;
  };

  const parseEmployeeSettings = (settingsString, defaults, settingType) => {
    try {
      if (!settingsString) {
        return defaults;
      }

      const parsed = JSON.parse(settingsString);

      if (Array.isArray(parsed)) {
        const result = parsed.reduce(
          (acc, item) => {
            const key = Object.keys(item)[0];
            const value = item[key];
            const booleanValue = value === "true" || value === true;
            acc[key] = booleanValue;
            return acc;
          },
          { ...defaults }
        );

        return result;
      }

      return defaults;
    } catch (e) {
      return defaults;
    }
  };

  const fetchPayslipLabels = async (employee) => {
    try {
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollPayslip_PayrollPayslipData_PayrollPayslipLabelsList"
      );
      const preData = await response.json();

      const data = preData.filter((item) => item.companyKey === companyKey )

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch payslip labels");
      }

      const processedData = Array.isArray(data) ? data : [data];

      // Parse employee's earnings and deductions settings
      let employeeEarningsSettings = {};
      let employeeDeductionsSettings = {};

      if (employee) {
        employeeEarningsSettings = parseEmployeeSettings(employee.earnings);
        employeeDeductionsSettings = parseEmployeeSettings(employee.deductions);
      }

      // Process labels by section with filtering
      const labelsBySection = {
        employee: {},
        bank: {},
        earnings: {},
        deductions: {},
      };

      let updatedCompanyInfo = { ...companyInfo };
      let payslipTitle = "Payslip For the Month";

      processedData.forEach((item) => {
        const sectionName = item.sectionName?.toLowerCase();

        if (sectionName === "companyinfo") {
          updatedCompanyInfo = {
            ...updatedCompanyInfo,
            name: item.companyName || updatedCompanyInfo.name,
            address: item.companyAddress || updatedCompanyInfo.address,
            logo: item.companyLogo || updatedCompanyInfo.logo,
          };
        } else if (sectionName === "company") {
          payslipTitle = item.value || payslipTitle;
        } else if (sectionName === "employee") {
          labelsBySection.employee[item.id] = {
            label: item.value,
            key: getEmployeeFieldKey(item.value),
          };
        } else if (sectionName === "bank") {
          labelsBySection.bank[item.id] = {
            label: item.value,
            key: getBankFieldKey(item.value),
          };
        } else if (sectionName === "earnings") {
          // Only add if no employee data (initial load) OR if employee has this earning enabled
          if (!employee) {
            labelsBySection.earnings[item.id] = {
              label: item.value,
              key: getEarningsFieldKey(item.value),
            };
          } else if (employeeEarningsSettings[item.value] === true) {
            labelsBySection.earnings[item.id] = {
              label: item.value,
              key: getEarningsFieldKey(item.value),
            };
          }
        } else if (sectionName === "deductions") {
          // Only add if no employee data (initial load) OR if employee has this deduction enabled
          if (!employee) {
            labelsBySection.deductions[item.id] = {
              label: item.value,
              key: getDeductionsFieldKey(item.value),
            };
          } else if (employeeDeductionsSettings[item.value] === true) {
            labelsBySection.deductions[item.id] = {
              label: item.value,
              key: getDeductionsFieldKey(item.value),
            };
          }
        }
      });

      setCompanyInfo(updatedCompanyInfo);
      setPayslipLabels({
        payslipTitle,
        ...labelsBySection,
      });
    } catch (err) {
      // Handle error silently
    }
  };

  const checkMissingEmployeeSummaryDetails = (employee, salary) => {
    const missingDetails = [];

    // Check each field that appears in employee summary section
    Object.entries(payslipLabels.employee || {}).forEach(([id, labelInfo]) => {
      console.log("labelInfo, ", labelInfo);
      console.log("employee, ", employee);
      console.log("salary, ", salary);

      const value = getEmployeeFieldValue(employee, salary, labelInfo.key);

      if (
        !value ||
        value === "null" ||
        value === "N/A" ||
        value.toString().trim() === ""
      ) {
        missingDetails.push(`• ${labelInfo.label}`);
      }
    });

    return missingDetails;
  };

  const checkMissingBankDetails = (employee) => {
    const missingDetails = [];

    // Check each field that appears in bank details section
    Object.entries(payslipLabels.bank || {}).forEach(([id, labelInfo]) => {
      const value = getBankFieldValue(employee, labelInfo.key);

      if (
        !value ||
        value === "null" ||
        value === "N/A" ||
        value.toString().trim() === ""
      ) {
        missingDetails.push(`• ${labelInfo.label}`);
      }
    });

    return missingDetails;
  };

  const showValidationError = (title, message) => {
    // Using browser alert for error message only
    showMyAlert({
      type: "info", // "success" | "danger" | "warning" | "info"
      title: "Info",
      message: `${title}\n\n${message}\n\nPlease complete all required details before generating payslip.`,
      duration: 2500, // auto-close in 2.5s
    });
  };

  // Helper functions to map labels to data fields
  const getEmployeeFieldKey = (label) => {
    const mapping = {
      "Employee Name": "fullName",
      "Employee ID": "employeeId",
      "Pay Period": "payPeriod",
      "Pay Date": "payDate",
      "Date of Joining": "dateOfJoining",
      Designation: "jobTitle",
      "Mobile Number": "userMobile",
      Email: "email",
      "Paid Days": "paidDays",
      "Alternate Mobile Number": "alternateMobile",
    };
    return mapping[label] || "XXX";
  };

  const getBankFieldKey = (label) => {
    const mapping = {
      "Bank Name": "bankName",
      "Account No": "accountNumber",
      "IFSC Code": "ifscCode",
      "Branch Name": "branchName",
    };
    return mapping[label] || "XXX";
  };

  const getEarningsFieldKey = (label) => {
    const mapping = {
      Basic: "basicSalary",
      "House Rent Allowance": "hra",
      Incentive: "incentive",
      "Travel Allowance": "travelAllowance",
    };
    return mapping[label] || 0;
  };

  const getDeductionsFieldKey = (label) => {
    const mapping = {
      "Income Tax": "incomeTax",
      PF: "providentfund",
      "Provident Fund": "providentfund",
      Provident: "providentfund",
      "Loss of Pay": "lossofpay",
      LOP: "lossofpay",
    };

    return mapping[label] || 0;
  };

  const handleNumericInput = (value) => {
    if (value === "") return "";

    const cleaned = value.replace(/[^0-9.]/g, "");

    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }

    return cleaned;
  };

  const handleEarningsChange = (id, value) => {
    const cleanedValue = handleNumericInput(value);
    const numericValue =
      cleanedValue === "" ? "" : parseFloat(cleanedValue) || 0;

    setEditableEarnings((prev) => ({
      ...prev,
      [id]: numericValue,
    }));
  };

  const handleDeductionsChange = (id, value) => {
    const cleanedValue = handleNumericInput(value);
    const numericValue =
      cleanedValue === "" ? "" : parseFloat(cleanedValue) || 0;

    setEditableDeductions((prev) => ({
      ...prev,
      [id]: numericValue,
    }));
  };

  // Updated submit function to save as dynamic arrays
  const handleSubmitValues = async () => {
    try {
      setLoading(true);

      if (!employeeSalary) {
        throw new Error("No salary data available");
      }

      const updatedSalary = {
        ...employeeSalary,
        id: employeeSalary.id,
      };

      // Build dynamic earnings array
      const newDynamicEarnings = [];
      let totalEarnings = 0;

      Object.entries(payslipLabels.earnings || {}).forEach(
        ([id, labelInfo]) => {
          const fieldValue = parseFloat(editableEarnings[id]) || 0;
          const labelKey = labelInfo.label;

          newDynamicEarnings.push({
            key: labelKey,
            value: fieldValue,
            label: labelKey,
            type: "earning",
          });

          totalEarnings += fieldValue;
        }
      );

      // Build dynamic deductions array
      const newDynamicDeductions = [];
      let totalDeductions = 0;

      Object.entries(payslipLabels.deductions || {}).forEach(
        ([id, labelInfo]) => {
          const fieldValue = parseFloat(editableDeductions[id]) || 0;
          const labelKey = labelInfo.label;

          newDynamicDeductions.push({
            key: labelKey,
            value: fieldValue,
            label: labelKey,
            type: "deduction",
          });

          totalDeductions += fieldValue;
        }
      );

      // Calculate net payable amount
      const existingCalculatedSalary =
        parseFloat(employeeSalary.overallcalculatedSalary) || 0;
      const netPayableAmount =
        existingCalculatedSalary + totalEarnings - totalDeductions;

      // Save dynamic data as JSON strings
      updatedSalary.dynamicEarnings = JSON.stringify(newDynamicEarnings);
      updatedSalary.dynamicDeductions = JSON.stringify(newDynamicDeductions);
      updatedSalary.netpayableamount = netPayableAmount.toString();
      updatedSalary.totalearnings = totalEarnings.toString();
      updatedSalary.totaldeductions = totalDeductions.toString();

      if (!updatedSalary.id) {
        throw new Error("Salary ID is missing");
      }

      // Update the salary data in the API
      const apiUrl = `https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList/${employeeSalary.id}`;

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
        body: JSON.stringify(updatedSalary),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          if (responseText) {
            errorMessage = responseText;
          }
        }

        throw new Error(`Failed to update salary values: ${errorMessage}`);
      }

      doAuditLog("Update", "Payslip Generator", employeeSalary, updatedSalary);

      // Update local state with new dynamic data
      setDynamicEarnings(newDynamicEarnings);
      setDynamicDeductions(newDynamicDeductions);
      setEmployeeSalary(updatedSalary); // Update the salary state
      setIsEditing(false);
      addToast("Salary values updated successfully", "success");
    } catch (err) {
      let errorMessage = "Failed to update salary values";
      if (err.message) {
        errorMessage += `: ${err.message}`;
      }

      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Updated postPayslipDataToAPI function to use dynamic data
  const postPayslipDataToAPI = async (employee, salary) => {
    try {
      // Build dynamic payslip data object using current values
      const payslipData = {
        // Company Info Section
        companyInfo: {
          companyName: companyInfo.name || "Dremerz Consultancy & Services",
          companyAddress:
            companyInfo.address ||
            "No:2/14, Kaveri Nagar Koladi Road, Thiruverkadu, Chennai -600077 India",
          companyLogo: companyInfo.logo || "",
          payslipTitle: payslipLabels.payslipTitle || "Payslip For the Month",
          payPeriod: `(${salary.startDate})-(${salary.endDate})`,
        },

        // Employee Summary Section
        employeeSummary: Object.entries(payslipLabels.employee || {}).reduce(
          (acc, [id, labelInfo]) => {
            const value = getEmployeeFieldValue(
              employee,
              salary,
              labelInfo.key
            );
            acc[labelInfo.label] = {
              key: labelInfo.key,
              value: value,
              displayValue: value,
            };
            return acc;
          },
          {
            "Paid Days": {
              key: "workingDays",
              value: salary.workingDays || 0,
              displayValue: salary.workingDays || "N/A",
            },
          }
        ),

        // Bank Details Section
        bankDetails: Object.entries(payslipLabels.bank || {}).reduce(
          (acc, [id, labelInfo]) => {
            const value = getBankFieldValue(employee, labelInfo.key);
            acc[labelInfo.label] = {
              key: labelInfo.key,
              value: value,
              displayValue: value,
            };
            return acc;
          },
          {}
        ),

        // Dynamic Earnings Section - Use current values
        earnings: Object.entries(payslipLabels.earnings || {}).reduce(
          (acc, [id, labelInfo]) => {
            const value = getCurrentEarningsValue(id, labelInfo);

            acc[labelInfo.label] = {
              key: labelInfo.label,
              value: value,
              displayValue: `₹${value.toLocaleString("en-IN")}`,
              amount: value,
            };
            return acc;
          },
          {}
        ),

        // Dynamic Deductions Section - Use current values
        deductions: Object.entries(payslipLabels.deductions || {}).reduce(
          (acc, [id, labelInfo]) => {
            const value = getCurrentDeductionsValue(id, labelInfo);

            acc[labelInfo.label] = {
              key: labelInfo.label,
              value: value,
              displayValue: `₹${value.toLocaleString("en-IN")}`,
              amount: value,
            };
            return acc;
          },
          {}
        ),

        // Net Payable Amount Section
        netPayableAmount: (() => {
          const totalEarnings = Object.entries(
            payslipLabels.earnings || {}
          ).reduce((sum, [id, labelInfo]) => {
            return sum + getCurrentEarningsValue(id, labelInfo);
          }, 0);

          const totalDeductions = Object.entries(
            payslipLabels.deductions || {}
          ).reduce((sum, [id, labelInfo]) => {
            return sum + getCurrentDeductionsValue(id, labelInfo);
          }, 0);

          const netAmount = totalEarnings - totalDeductions;

          return {
            "Gross Earnings": {
              key: "totalEarnings",
              value: totalEarnings,
              displayValue: `₹${totalEarnings.toLocaleString("en-IN")}`,
              amount: totalEarnings,
            },
            "Total Deductions": {
              key: "totalDeductions",
              value: totalDeductions,
              displayValue: `₹${totalDeductions.toLocaleString("en-IN")}`,
              amount: totalDeductions,
            },
            "Net Payable Amount": {
              key: "netAmount",
              value: netAmount,
              displayValue: `₹${netAmount.toLocaleString("en-IN")}`,
              amount: netAmount,
              amountInWords: numberToWords(Math.round(netAmount)),
            },
          };
        })(),

        // Dynamic data arrays for future reference
        dynamicData: {
          earnings: dynamicEarnings,
          deductions: dynamicDeductions,
        },

        // Additional metadata
        // Additional metadata
        metadata: {
          employeeId: employee.employeeId || employee.employee_id,
          employeeMobile: employee.userMobile || employee.personalMobile,
          salaryId: salary.id,
          payPeriodStart: salary.startDate,
          payPeriodEnd: salary.endDate,
          generatedBy: sessionStorage.getItem("userMobile") || "N/A",
          generatedAt: new Date().toISOString(),
          payslipGeneratedAt: new Date().toISOString(), // Add this field
          // payslipVersion: '2.0' // Updated version for dynamic data
        },
      };

      // Post to the API
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8924_PayrollProduct_PayrollPayslip_PayrollPayslipData_PayrollPayslipDataList",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          body: JSON.stringify({ ...payslipData, companyKey }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to save payslip data: ${response.status} - ${errorText}`
        );
      }

      const responseData = await response.json();
      doAuditLog("Add", "Payslip Generator", "", payslipData);
      return responseData;
    } catch (error) {
      throw error;
    }
  };

  const getEmployeeFieldValue = (employee, salary, key) => {
    switch (key) {
      case "fullName":
        return employee.fullName || employee.full_name || "N/A";
      case "employeeId":
        return employee.employeeId || employee.employee_id || "N/A";
      case "payPeriod":
        const periodStart = salary.startDate;
        const periodEnd = salary.endDate;
        if (periodStart && periodEnd) {
          // Format as "01 Jan - 31 Jan 2024"
          const start = new Date(periodStart);
          const end = new Date(periodEnd);

          const startFormatted = start.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
          });
          const endFormatted = end.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          return `${startFormatted} - ${endFormatted}`;
        }
        return salary.workingDays ? `${salary.workingDays} Days` : "N/A";
      case "payDate":
        // This will now be handled separately in the template
        return "See Pay Date below";
      case "dateOfJoining":
        const doj = employee.dateOfJoining || employee.date_of_joining;
        return doj ? formatDate(doj) : "N/A";
      case "jobTitle":
        return employee.jobTitle || "N/A";
      case "email":
        return salary.email || "N/A";
      case "userMobile":
        return (
          employee.userMobile ||
          employee.personalMobile ||
          employee.mobile_number ||
          "N/A"
        );
      default:
        return employee[key] || "N/A";
    }
  };

  const getBankFieldValue = (employee, key) => {
    switch (key) {
      case "bankName":
        return employee.bankName || employee.bank_name || "N/A";
      case "accountNumber":
        return employee.accountNumber || employee.account_number || "N/A";
      case "ifscCode":
        return employee.ifscCode || employee.ifsc_code || "N/A";
      case "branchName":
        return employee.branchName || employee.branch_name || "N/A";
      default:
        return employee[key] || "N/A";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-IN", options);
  };

  const numberToWords = (num) => {
    if (num === 0) return "Zero Rupees Only";

    const units = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "Ten",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    function convertLessThanOneThousand(num) {
      if (num === 0) return "";
      if (num < 10) return units[num];
      if (num < 20) return teens[num - 10];
      if (num < 100)
        return (
          tens[Math.floor(num / 10)] +
          (num % 10 !== 0 ? " " + units[num % 10] : "")
        );
      return (
        units[Math.floor(num / 100)] +
        " Hundred" +
        (num % 100 !== 0 ? " and " + convertLessThanOneThousand(num % 100) : "")
      );
    }

    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const remainder = num % 1000;

    let result = "";
    if (crore > 0) result += convertLessThanOneThousand(crore) + " Crore ";
    if (lakh > 0) result += convertLessThanOneThousand(lakh) + " Lakh ";
    if (thousand > 0)
      result += convertLessThanOneThousand(thousand) + " Thousand ";
    if (remainder > 0) result += convertLessThanOneThousand(remainder);

    return result.trim() + " Rupees Only";
  };

  const generatePayslipPDF = async (employee, salary) => {
    return new Promise(async (resolve, reject) => {
      try {
        const payslipId = `payslip-content-${employee.employeeId}-${salary.startDate}-${salary.endDate}`;

        const tempContainer = document.createElement("div");
        tempContainer.style.position = "absolute";
        tempContainer.style.left = "-9999px";
        tempContainer.style.width = "210mm";
        tempContainer.style.background = "white";

        document.body.appendChild(tempContainer);

        const root = ReactDOM.createRoot(tempContainer);

        root.render(
          <PayslipTemplate
            employee={employee}
            salary={salary}
            showActions={false}
            editableEarnings={editableEarnings || {}}
            editableDeductions={editableDeductions || {}}
            isEditing={false}
            handleEarningsChange={() => {}}
            handleDeductionsChange={() => {}}
            payslipLabels={payslipLabels}
            companyInfo={companyInfo}
            getEmployeeFieldValue={getEmployeeFieldValue}
            getBankFieldValue={getBankFieldValue}
            getDynamicEarningsValue={getDynamicEarningsValue}
            getDynamicDeductionsValue={getDynamicDeductionsValue}
            dynamicEarnings={dynamicEarnings}
            dynamicDeductions={dynamicDeductions}
            getCurrentEarningsValue={getCurrentEarningsValue}
            getCurrentDeductionsValue={getCurrentDeductionsValue}
            numberToWords={numberToWords}
          />
        );

        // Wait for React to render and images to load
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Find the actual payslip element
        const payslipElement =
          tempContainer.querySelector(`#${payslipId}`) ||
          tempContainer.firstChild;

        // Generate the PDF with proper dimensions
        const canvas = await html2canvas(payslipElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          windowWidth: 794,
          windowHeight: payslipElement.scrollHeight,
          width: payslipElement.offsetWidth,
          height: payslipElement.scrollHeight,
        });

        // Clean up
        root.unmount();
        document.body.removeChild(tempContainer);

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Only add to one page if content fits
        if (imgHeight <= pageHeight) {
          // Content fits in one page
          pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
        } else {
          // Content needs multiple pages (shouldn't happen with optimized template)
          let heightLeft = imgHeight;
          let position = 0;

          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
        }

        const pdfBlob = pdf.output("blob");
        resolve(pdfBlob);
      } catch (err) {
        console.error("PDF Generation Error:", err);
        reject(err);
      }
    });
  };

  const handleGeneratePayslip = async () => {
    if (!employeeDetails || !employeeSalary) {
      addToast("No employee or salary data available", "error");
      return;
    }

    try {
      // Check employee summary details - MUST be complete
      const missingEmployeeDetails = checkMissingEmployeeSummaryDetails(
        employeeDetails,
        employeeSalary
      );

      if (missingEmployeeDetails.length > 0) {
        const employeeDetailsMessage = `Please add employee details properly.\n\nMissing Details:\n${missingEmployeeDetails.join(
          "\n"
        )}`;

        showValidationError("Employee Details Missing", employeeDetailsMessage);
        // addToast(
        //   "Cannot generate payslip - Employee details incomplete",
        //   "error"
        // );
        return; // Stop execution - do not generate payslip
      }

      // Check bank details - MUST be complete
      const missingBankDetails = checkMissingBankDetails(employeeDetails);

      if (missingBankDetails.length > 0) {
        const bankDetailsMessage = `Please add bank details properly.\n\nMissing Details:\n${missingBankDetails.join(
          "\n"
        )}`;

        showValidationError("Bank Details Missing", bankDetailsMessage);
        addToast("Cannot generate payslip - Bank details incomplete", "error");
        return; // Stop execution - do not generate payslip
      }

      // Only proceed if ALL details are complete
      setLoading(true);

      // If in edit mode, save values first
      if (isEditing) {
        await handleSubmitValues();
        // Wait a moment for state to update
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      await sendPayslipToEmployee();
      addToast("Payslip generated and saved successfully", "success");

      setTimeout(() => {
        navigate("/payslip-generate");
      }, 1500);
    } catch (error) {
      console.error("Error generating payslip:", error);
      addToast("Failed to generate payslip", "error");
    } finally {
      setLoading(false);
    }
  };

  const checkMissingEmployeeDetails = (employee) => {
    const missingDetails = [];

    if (
      !employee.fullName ||
      employee.fullName === "null" ||
      employee.fullName.trim() === ""
    ) {
      missingDetails.push("• Employee Name");
    }
    if (
      !employee.employeeId ||
      employee.employeeId === "null" ||
      employee.employeeId.trim() === ""
    ) {
      missingDetails.push("• Employee ID");
    }
    if (
      !employee.jobTitle ||
      employee.jobTitle === "null" ||
      employee.jobTitle.trim() === ""
    ) {
      missingDetails.push("• Job Title/Designation");
    }
    // if (!employee.dateOfJoining || employee.dateOfJoining === 'null' || employee.dateOfJoining.trim() === '') {
    //   missingDetails.push('• Date of Joining');
    // }
    if (!employee.userMobile && !employee.personalMobile) {
      missingDetails.push("• Mobile Number");
    }

    if (
      !employee.bankName ||
      employee.bankName === "null" ||
      employee.bankName.trim() === ""
    ) {
      missingDetails.push("• Bank Name");
    }
    if (
      !employee.accountNumber ||
      employee.accountNumber === "null" ||
      employee.accountNumber.trim() === ""
    ) {
      missingDetails.push("• Account Number");
    }
    if (
      !employee.ifscCode ||
      employee.ifscCode === "null" ||
      employee.ifscCode.trim() === ""
    ) {
      missingDetails.push("• IFSC Code");
    }
    if (
      !employee.branchName ||
      employee.branchName === "null" ||
      employee.branchName.trim() === ""
    ) {
      missingDetails.push("• Branch Name");
    }

    return missingDetails;
  };

  const sendPayslipToEmployee = async () => {
    if (!employeeDetails || !employeeSalary) return;

    try {
      setLoading(true);

      // 1. First post the payslip data to the new API
      await postPayslipDataToAPI(employeeDetails, employeeSalary);

      // 2. Generate the PDF
      const pdfBlob = await generatePayslipPDF(employeeDetails, employeeSalary);

      // 3. Create FormData to send the PDF to existing API
      const formData = new FormData();
      formData.append(
        "payslipPdf",
        pdfBlob,
        `Payslip_${employeeDetails.employeeId}_${employeeSalary.startDate}_${employeeSalary.endDate}.pdf`
      );

      // 4. Add payslipgeneratedat field with current date and time
      const currentDateTime = new Date().toISOString();
      formData.append("payslipgeneratedat", currentDateTime);

      // 5. Make PUT request to update the payslip PDF in existing API
      const response = await fetch(
        `https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList/${employeeSalary.id}`,
        {
          method: "PUT",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save payslip PDF to existing API");
      }
      const formDataObj = Object.fromEntries(formData.entries());
      doAuditLog("Update", "Payslip Generator", employeeSalary, {
        ...employeeSalary,
        ...formDataObj,
      });

      // 6. Update local state with the generated timestamp
      setEmployeeSalary((prevSalary) => ({
        ...prevSalary,
        payslipgeneratedat: currentDateTime,
      }));

      const successMessage = `Payslip for ${formatPeriod(
        employeeSalary.startDate,
        employeeSalary.endDate
      )} has been generated and saved successfully`;
      addToast(successMessage, "success");

      // Return success to indicate completion
      return { success: true, message: successMessage };
    } catch (err) {
      const errorMessage = `Failed to generate payslip: ${err.message}`;
      addToast(errorMessage, "error");
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    })}`;
  };

  if (loading) {
    return (
      <div className="container-fluid px-4 py-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <Spinner className="text-primary" size="lg" />
            <p className="mt-3 text-muted">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
        <Button
          variant="primary"
          onClick={() => {
            setError("");
            if (salaryId) {
              fetchSalaryAndEmployeeData(salaryId);
            }
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="h3 mb-0 text-dark fw-bold">Payslip Generator</h1>
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              Back to List
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          {employeeDetails && (
            <Card className="mb-4">
              <Card.Header>
                <h5>Employee Information</h5>
              </Card.Header>
              <Card.Body>
                <div>
                  <div className="d-flex mb-2">
                    <span className="fw-bold">Name:</span>
                    <span className="ms-2">{employeeDetails.fullName}</span>
                  </div>
                  <div className="d-flex mb-2">
                    <span className="fw-bold">Employee ID:</span>
                    <span className="ms-2">{employeeDetails.employeeId}</span>
                  </div>
                  <div className="d-flex mb-2">
                    <span className="fw-bold">Department:</span>
                    <span className="ms-2">{employeeDetails.department}</span>
                  </div>
                  <div className="d-flex mb-2">
                    <span className="fw-bold">Designation:</span>
                    <span className="ms-2">{employeeDetails.jobTitle}</span>
                  </div>
                  <div className="d-flex mb-2">
                    <span className="fw-bold">Location:</span>
                    <span className="ms-2">{employeeDetails.workLocation}</span>
                  </div>
                  <div className="d-flex mb-2">
                    <span className="fw-bold">Email:</span>
                    <span className="ms-2">
                      {employeeDetails.personalEmail}
                    </span>
                  </div>
                  <div className="d-flex mb-3">
                    <span className="fw-bold">Mobile:</span>
                    <span className="ms-2">{employeeDetails.userMobile}</span>
                  </div>
                </div>

                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    onClick={handleGeneratePayslip}
                    disabled={loading || !employeeDetails || !employeeSalary}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Generating...
                      </>
                    ) : employeeSalary?.payslipPdf ? (
                      "Regenerate Payslip"
                    ) : (
                      "Generate Payslip"
                    )}
                  </Button>
                  {/* <Button
                    variant="info"
                    onClick={() => downloadPayslipAsPDF()}
                    disabled={
                      !employeeDetails ||
                      !employeeSalary ||
                      !employeeSalary?.payslipPdf
                    }
                  >
                    Download PDF
                  </Button> */}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col md={8} ref={payslipPreviewRef}>
          <Card>
            <Card.Header>
              <h5>Payslip Preview</h5>
            </Card.Header>
            <Card.Body>
              {employeeDetails && employeeSalary ? (
                <>
                  <PayslipTemplate
                    employee={employeeDetails}
                    salary={employeeSalary}
                    showActions={false}
                    editableEarnings={editableEarnings}
                    editableDeductions={editableDeductions}
                    isEditing={isEditing}
                    handleEarningsChange={handleEarningsChange}
                    handleDeductionsChange={handleDeductionsChange}
                    payslipLabels={payslipLabels}
                    companyInfo={companyInfo}
                    getEmployeeFieldValue={getEmployeeFieldValue}
                    getBankFieldValue={getBankFieldValue}
                    getDynamicEarningsValue={getDynamicEarningsValue}
                    getDynamicDeductionsValue={getDynamicDeductionsValue}
                    getCurrentEarningsValue={getCurrentEarningsValue}
                    getCurrentDeductionsValue={getCurrentDeductionsValue}
                    dynamicEarnings={dynamicEarnings}
                    dynamicDeductions={dynamicDeductions}
                    numberToWords={numberToWords}
                    checkMissingEmployeeDetails={checkMissingEmployeeDetails}
                  />

                  <div className="d-flex justify-content-center gap-3 mt-4">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmitValues}
                          disabled={loading}
                          style={{ transition: "none", minWidth: "120px" }}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Saving...
                            </>
                          ) : (
                            "Save Values"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setIsEditing(false)}
                          style={{ transition: "none", minWidth: "80px" }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setIsEditing(true)}
                          style={{ transition: "none", minWidth: "100px" }}
                        >
                          Edit Values
                        </button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <div className="display-4 mb-3">📄</div>
                  <h3>Loading payslip data...</h3>
                  <p>Please wait while we fetch the employee details</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PayslipGenerator;
